// Auswertung des Moduls „Erinnerungen“ in der Suite.
//
// Hier stehen Zahlen, keine Personen: wie viele Erinnerungen je Sendung vorgemerkt
// sind, wie viele Mitteilungen herausgegangen sind und wie viele abgelaufene
// Anmeldungen aufgeräumt wurden. Wer sich eingetragen hat, steht nirgends – und
// lässt sich hier auch nicht nachschlagen.

import { useEffect, useState, type ReactNode } from "react";
import { remindersApi, type ReminderReport } from "../api";
import { ChannelPicker, useChannel } from "./social-ui";
import { errorText } from "./streaming-ui";

function Zahl({ label, value, hint }: { label: string; value: number; hint?: string }): ReactNode {
  return (
    <div className="well">
      <div className="text-2xl font-semibold leading-tight tracking-tight tabular-nums text-primary">{value}</div>
      <div className="mt-0.5 text-xs font-medium text-secondary">{label}</div>
      {hint && <div className="mt-0.5 text-xs text-muted">{hint}</div>}
    </div>
  );
}

function termin(iso: string | null): string {
  if (!iso) return "steht nicht im Sendeplan";
  return new Date(iso).toLocaleString("de-DE", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }) + " Uhr";
}

export function ReminderReportView(): ReactNode {
  const picker = useChannel();
  const { slug } = picker;
  const [days, setDays] = useState(30);
  const [data, setData] = useState<ReminderReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setData(null);
    setError(null);
    remindersApi.report(slug, days).then(setData).catch((err) => setError(errorText(err)));
  }, [slug, days]);

  const vorgemerkt = (data?.shows ?? []).reduce((sum, s) => sum + s.reminders, 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ChannelPicker {...picker} />
        <label className="flex items-center gap-2 text-sm text-secondary">
          Zeitraum{" "}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="input !w-auto !h-9 pr-8">
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
      </div>
      {error && <div className="callout callout-danger">{error}</div>}
      {!data ? (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Lade …">{[0, 1, 2, 3].map((i) => <div key={i} className="h-[74px] animate-pulse rounded-lg bg-input" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Zahl label="vorgemerkt für Sendungen" value={vorgemerkt} />
            <Zahl label="vorgemerkt für Wünsche" value={data.requests} />
            <Zahl label="zugestellt" value={data.delivered} hint={`in ${data.days} Tagen`} />
            <Zahl label="aufgeräumt" value={data.cleaned} hint="abgelaufene Anmeldungen" />
          </div>
          {data.failed > 0 && (
            <p className="text-sm text-muted">
              {data.failed} Mitteilungen kamen nicht an. Einzelne Ausfälle sind normal – bleibt die Zahl hoch,
              lohnt ein Blick ins Protokoll der API.
            </p>
          )}
          <div>
            <h3 className="section-label mb-2">Je Sendung</h3>
            {data.shows.length === 0 ? (
              <p className="text-sm text-muted">
                Im Sendeplan dieses Kanals steht noch keine Sendung. Erst dann können Hörer sich erinnern lassen.
              </p>
            ) : (
              <ul className="divide-y divide-line overflow-hidden rounded-lg border border-default text-sm">
                {data.shows.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2.5">
                    <span className="min-w-0 flex-1 truncate font-medium text-primary">{s.title}</span>
                    <span className="text-muted">nächste Sendung: {termin(s.next)}</span>
                    <span className="badge badge-neutral tabular-nums">
                      {s.reminders === 1 ? "1 Erinnerung" : `${s.reminders} Erinnerungen`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="text-xs text-muted">
            Gespeichert ist nur, was zum Verschicken nötig ist. Wer sich eingetragen hat, lässt sich hier nicht
            nachschlagen – und über diesen Weg geht nur, worum jemand gebeten hat: keine Werbung, keine Programmhinweise.
          </p>
        </>
      )}
    </div>
  );
}
